import { useCallback } from 'react';
import type { SearchSuggestion } from '../../favorites-app/types';
import CompaniesFavoriteCount from '../components/CompaniesFavoriteCount';
import OnboardingTickerSearch from '../components/OnboardingTickerSearch';
import SelectedFavoriteChips from '../components/SelectedFavoriteChips';
import { ONBOARDING_SCROLL_AREA_CLASS, ONBOARDING_STEP_BODY_CLASS } from '../constants';

interface CompaniesStepProps {
  selected: SearchSuggestion[];
  isActive: boolean;
  isLoadingFavorites?: boolean;
  onSelect: (stock: SearchSuggestion) => void;
  onRemove: (tickerSymbol: string) => void;
  onCompanyAdded?: (tickerSymbol: string) => void;
}

export default function CompaniesStep({
  selected,
  isActive,
  isLoadingFavorites = false,
  onSelect,
  onRemove,
  onCompanyAdded,
}: CompaniesStepProps) {
  const handleSelect = useCallback(
    (stock: SearchSuggestion) => {
      const alreadySelected = selected.some(
        (company) =>
          company.ticker_symbol.toLowerCase() === stock.ticker_symbol.toLowerCase(),
      );
      if (alreadySelected) {
        return;
      }

      onSelect(stock);
      onCompanyAdded?.(stock.ticker_symbol);
    },
    [onCompanyAdded, onSelect, selected],
  );

  const horizontalInsetClass = 'px-1 sm:px-0';
  const isEmpty = selected.length === 0;
  const showFavoriteCount = isLoadingFavorites || selected.length > 0;

  return (
    <div className={ONBOARDING_STEP_BODY_CLASS}>
      <div
        className={`mx-auto flex w-full shrink-0 flex-col ${horizontalInsetClass} ${
          isEmpty ? 'pb-2 sm:max-w-lg sm:pb-4' : 'space-y-4 pb-1'
        }`}
      >
        <div className="relative z-20 flex shrink-0 justify-center">
          <OnboardingTickerSearch
            selected={selected}
            isActive={isActive}
            onSelect={handleSelect}
            variant="footer"
          />
        </div>

        {showFavoriteCount ? (
          <CompaniesFavoriteCount count={selected.length} isLoading={isLoadingFavorites} />
        ) : null}
      </div>

      <div className={`${horizontalInsetClass} ${ONBOARDING_SCROLL_AREA_CLASS}`}>
        <SelectedFavoriteChips
          selected={selected}
          isLoadingFavorites={isLoadingFavorites}
          onRemove={onRemove}
        />
      </div>
    </div>
  );
}
